import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  onlyIfForm,
  useSelector,
} from '../../../store.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import api from '../../../common/api.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {setUser, userSelector} from '../../../data/users.js'
import TextInput from '../../../common/components/TextInput.js'
import {isPresent} from '../../../common/utils.js'
import {formatAbodeURL} from '../../../common/abode.js'
import copyTextToClipboard from '../../../common/copyTextToClipboard.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'

export const MODAL_FORM = 'setup_mfa_modal'

export function showSetupMFAModal(userID) {
  setForm(MODAL_FORM, {
    userID,
    otp: '',
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

function errorSelector(state) {
  const {otp} = modalFormSelector(state)
  const errors = {}

  if (!isPresent(otp) || otp.length !== 6) {
    errors.otp = 'Provide 6 digit code'
    errors.preventSave = true
  }

  return errors
}

function qrCodeURLSelector(state) {
  const {userID} = modalFormSelector(state)
  const user = userSelector(state, {userID})

  const params = {
    file_name: 'mfa.svg',
    render: JSON.stringify([
      'QRCodeSVG',
      {
        url: user.otp_uri,
      },
    ]),
  }

  return formatAbodeURL('/render', params)
}

export async function saveMFA() {
  try {
    const {userID, otp} = modalFormSelector(getState())
    const user = userSelector(getState(), {userID})

    updateModalForm({isSaving: true, serverError: null})

    const params = {
      otp_seed: user.otp_seed,
      otp,
    }

    const {json} = await api.put(`/user/${userID}`, params)

    setUser(json)

    closeModal()

    showMessageToast('MFA has been enabled.')
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function SetupMFAModal({form}) {
  const user = useSelector((state) =>
    userSelector(state, {userID: form.userID}),
  )
  const errors = useSelector(errorSelector)
  const qrCodeURL = useSelector(qrCodeURLSelector)

  return (
    <ConfirmModal
      title="Enable Multi-Factor Auth"
      onConfirm={() => saveMFA()}
      onCancel={() => closeModal()}
      confirmText="Enable MFA"
      cancelText="Cancel"
      helpLink="https://support.ordoro.com/how-to-enable-multi-factor-authentication-mfa-in-ordoro/"
      helpTitle="Get Help with Configuring MFA"
      isSaving={form.isSaving}
      error={form.serverError}
      isDisabled={errors.preventSave}
    >
      <ol className="margin-bottom-20">
        <li className="list__item divider--bottom margin-bottom-30 padding-bottom-25">
          <p className="fs-01 lh-md margin-bottom-5">
            <strong>
              Configure your Authenticator app with your unique Ordoro
              Authentication URI.
            </strong>
          </p>
          <p className="fs-n0 lh-md margin-bottom-25">
            Either use the following button to copy the URI code or take a photo
            of the QR code from within your mobile Authenticator app.
          </p>

          <div className="flex align-items-center margin-bottom-0">
            <div className="margin-right-30">
              <ButtonPrimary
                isOutlined
                size="md"
                onClick={() => {
                  copyTextToClipboard(user.otp_uri)

                  showMessageToast(
                    'Authentication URI code copied to clipboard.',
                  )
                }}
              >
                <strong>Copy Authentication URI Code</strong>
              </ButtonPrimary>
            </div>
            <div className="w-25">
              <div>
                <img alt="QR Code" src={qrCodeURL} />
              </div>
            </div>
          </div>
        </li>
        <li className="list__item">
          <p className="fs-01 lh-md margin-bottom-5">
            <strong>
              To finish enabling MFA, enter a valid 6-digit OTP Code.
            </strong>
          </p>
          <p className="fs-n0 lh-md margin-bottom-20">
            Once the URI has successfully been added to your Authenticator app,
            it will start to continually provide One-Time-Passwords (OTPs) in
            the form of 6-digit codes. Enter a valid one in the following input
            to complete the setup.
          </p>
          <div className="w-50">
            <TextInput
              className="input--tall"
              label="6-Digit OTP Code"
              id="otp"
              value={form.otp}
              onChange={(otp) => updateModalForm({otp})}
            />
          </div>
        </li>
      </ol>
    </ConfirmModal>
  )
}

export default onlyIfForm(SetupMFAModal, modalFormSelector)
